import { FC, useMemo } from 'react';
import { Button, Divider, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { Subscription, Tier } from 'types/subscription.types';
import { CreateSubscriptionOverlay } from 'app/components/Subscription/CreateSubscriptionOverlay';
import { StorageWidget } from 'app/components/Storage/StorageWidget';
import { DDL } from 'types/general.types';
import { getStorageLimitText, getCurrentTierLabel } from './utils';
import { storageComponentSx } from './styles';
import { StorageWidgetProps } from './types';

export interface StorageComponentProps {
  storage: StorageWidgetProps;
  subscription: Subscription;
  tierList: Tier[];
  subscriptionTiersDDL: DDL;
  nextTier?: Tier;
  onManageSubscriptionClick: () => Promise<void>;
  onUpgradeSubscriptionClick: () => void;
}

export const StorageComponent: FC<StorageComponentProps> = ({
  subscription,
  storage,
  tierList,
  subscriptionTiersDDL,
  nextTier,
  onManageSubscriptionClick,
  onUpgradeSubscriptionClick,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isOver = useMemo(
    () =>
      storage.status === 'OverLimit' || (!nextTier && storage.freeBytes <= 0),
    [storage, nextTier]
  );

  return (
    <Flex sx={storageComponentSx.root}>
      <Flex alignSelf="flex-start" sx={storageComponentSx.planWrapper}>
        <Text fontWeight={500} color="M.70">
          Plan
        </Text>
        <Flex sx={storageComponentSx.planDetails}>
          <Text color="M.60" fontSize="xs" data-testid="current-subscription">
            {getCurrentTierLabel(subscription, subscriptionTiersDDL)}
          </Text>
          <Text color="M.60" fontSize="xs" data-testid="storage-space-text">
            &nbsp;-&nbsp;{storage.total} {storage.totalUnit} storage.
          </Text>
        </Flex>
      </Flex>
      <Divider
        orientation="vertical"
        display={{ base: 'none', display: 'block' }}
      />
      <Flex sx={storageComponentSx.widgetWrapper}>
        <Text
          fontWeight={500}
          color="M.70"
          data-testid="storage-status-text"
          mb={0.5}
        >
          {getStorageLimitText(storage.status, isOver)}
        </Text>
        <StorageWidget {...storage} isOver={isOver} />
      </Flex>
      <Flex ml="auto">
        <Button
          variant="primary"
          size="md"
          sx={storageComponentSx.subscriptionButton}
          onClick={() => {
            if (subscription?.hasSubscription) {
              onManageSubscriptionClick();
              return;
            }
            onUpgradeSubscriptionClick();
            onOpen();
          }}
          isDisabled={!subscription.hasSubscription && tierList.length === 0}
        >
          {subscription?.hasSubscription ? 'Manage' : 'Upgrade'}
        </Button>
      </Flex>
      <CreateSubscriptionOverlay
        tiers={tierList}
        open={isOpen}
        onClose={onClose}
      />
    </Flex>
  );
};
